import {
  Box,
  Stack,
  Paper,
  Typography,
  styled,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  IWithClassName,
  IWithThemeProps,
  event,
} from "../../../theme/default.theme";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import InstallMobileOutlinedIcon from "@mui/icons-material/InstallMobileOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { LineChart } from "@mui/x-charts/LineChart";
import SquareIcon from "@mui/icons-material/Square";
import { Events } from "../../../enum/campaign";
import { useAppSelector } from "../../../redux/store";
import { isEmpty } from "lodash";
import dayjs, { Dayjs } from "dayjs";

const maxLimitEvents = 2;

const events = {
  [Events.Click]: "Clicks",
  [Events.Installs]: "Installs",
  [Events.CR]: "CR%",
  [Events.Spent]: "Spends",
};

const Metrics = ({ className }: IWithClassName) => {
  const {report} = useAppSelector((state) => state.report);
  const [selectedEvents, setSelectedEvents] = React.useState<Array<Events>>([Events.Click, Events.Installs]);
  const [timeline, setTimeLine] = useState<any>({});
  const [aggResult, setAggResult] = useState({
    clicks: 0, 
    installs: 0, 
    spend: 0,
    cr: "",
  });
  

  useEffect(() => {
    const result = {
      clicks: 0, 
      installs: 0, 
      spend: 0,
      cr: "",
    }
    
    if(report) {
      (report.data ?? []).forEach(({clicks, installs, spend, cr, created}) => {
        clicks && (result.clicks += clicks);
        installs && (result.installs += installs);
        spend && (result.spend += spend);
      });

      result.cr = ((result.installs / result.clicks) * 100).toFixed(2);

      setAggResult(result);
      
      const graphData = (report.data ?? []).reduce((dategroup, rep) => {
        if(dategroup.hasOwnProperty(rep.created)) {
          dategroup[rep.created]= {
            clicks: dategroup[rep.created].clicks + rep.clicks,
            installs: dategroup[rep.created].installs + rep.installs,
            spend: dategroup[rep.created].spend + (isNaN(rep.spend) ? 0 : Number(rep.spend)),
            cr: (((dategroup[rep.created].installs + rep.installs) / (dategroup[rep.created].clicks + rep.clicks)) * 100).toFixed(2),
          };
        } else {
          dategroup[rep.created] = { 
            clicks: rep.clicks,
            installs: rep.installs,
            spend: isNaN(rep.spend) ? 0 : Number(rep.spend),
            cr: ((rep.installs / rep.clicks) * 100).toFixed(2),
          };
        }
        
        return dategroup;
      },{} as any);
      
      const graphData2 = {
        [Events.Click]: [],
        [Events.CR]: [],
        [Events.Installs]: [],
        [Events.Spent]: [],
      } as {
        [Events.Click]: Array<number>;
        [Events.CR]: Array<number>;
        [Events.Installs]: Array<number>;
        [Events.Spent]: Array<number>;
      }
  
      const time = Object.keys(graphData).sort().map((key: string) => dayjs(key));
  
      time.forEach((node) => {
        const { clicks, installs, spend, cr } = graphData[node.format("YYYY-MM-DD")];
        
        graphData2.click.push(Number(clicks) ?? 0);
        graphData2.installs.push(Number(installs) ?? 0);
        graphData2.spent.push(Number(spend) ?? 0);
        graphData2.cr.push(Number(cr) ?? 0);
  
      });
      
      setTimeLine({ time, ...graphData2 })
    }

    
  }, [report]);

  const handleChange = (id: Events, checked: boolean) => {
    if (checked) {
      if (selectedEvents.length < maxLimitEvents) {
        setSelectedEvents((prev) => [...prev, id]);
      }
    } else {
      setSelectedEvents((prev) => prev.filter((name) => name !== id));
    }
  };

  return (
    <Box component={Paper} className={className}>
      <Typography variant="h6">Metrics</Typography>
      <Box className="tile-container">
        <Box className="tile">
          <span className="tile-icon click-icon">
            <TouchAppOutlinedIcon />
          </span>
          <span className="tile-text">
            <Typography variant="h6">{aggResult.clicks?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
            <Typography>Clicks</Typography>
          </span>
        </Box>
        <Box className="tile">
          <span className="tile-icon installs-icon">
            <InstallMobileOutlinedIcon />
          </span>
          <span className="tile-text">
            <Typography variant="h6">{aggResult.installs?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
            <Typography>Installs / Leads</Typography>
          </span>
        </Box>
        <Box className="tile">
          <span className="tile-icon cr-icon">
            <TableViewOutlinedIcon />
          </span>
          <span className="tile-text">
            <Typography variant="h6">{aggResult.cr?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%</Typography>
            <Typography>CR%</Typography>
          </span>
        </Box>
        <Box className="tile">
          <span className="tile-icon sent-icon">
            <RequestQuoteOutlinedIcon />
          </span>
          <span className="tile-text">
            <Typography variant="h6">${aggResult.spend?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
            <Typography>Spent</Typography>
          </span>
        </Box>
      </Box>

      {!isEmpty(timeline) && timeline.time.length > 0 && (<CampaignChat
        handleChange={handleChange}
        selectedEvents={selectedEvents}
        timeline={timeline}
      />)}
      
    </Box>
  );
};

interface ICampaignChat {
  handleChange: (id: Events, checked: boolean) => void;
  selectedEvents: Array<string>;
  timeline: any;
}

const CampaignChat = ({ handleChange, selectedEvents, timeline }: ICampaignChat) => {
  const availableCharts = useMemo(() => {
    return {
      [Events.Click]: {
        data: timeline[Events.Click],
        label: Events.Click,
        area: true,
        stack: "total",
        showMark: false,
        color: event.click.graphColor,
      },
      [Events.CR]: {
        data: timeline[Events.CR],
        label: Events.CR,
        area: true,
        stack: "total",
        showMark: false,
        color: event.cr.graphColor,
      },
      [Events.Installs]: {
        data: timeline[Events.Installs],
        label: Events.Installs,
        area: true,
        stack: "total",
        showMark: false,
        color: event.install.graphColor,
      },
      [Events.Spent]: {
        data: timeline[Events.Spent],
        label: Events.Spent,
        area: true,
        stack: "total",
        showMark: false,
        color: event.sent.graphColor,
      },
    };
  }, [timeline]);

  return (
    <Stack sx={{ width: "100%" }}>
      <Box className="event-box">
        {Object.keys(events).map((key) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                onChange={({ currentTarget }) =>
                  handleChange(key as Events, currentTarget.checked)
                }
                checkedIcon={<SquareIcon className="check-box-checked" />}
              />
            }
            label={events[key as Events]}
            checked={selectedEvents.includes(key)}
          />
        ))}
      </Box>
      <LineChart
        series={selectedEvents.map((event) => availableCharts[event as Events])}
        xAxis={[
          {
            scaleType: "point",
            data: timeline.time.map((date: Dayjs) => date.format("YYYY-MM-DD")),
          },
        ]}
        height={200}
        margin={{ top: 10, bottom: 20 }}
      />
    </Stack>
  );
};

export default styled(Metrics)(({ theme }: IWithThemeProps) => ({
  padding: theme?.spacing(2),
  display: "grid",
  gap: 10,
  ".tile-container": {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
  },
  ".tile": {
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    borderRadius: 5,
    backgroundColor: theme?.palette.background.default,
    boxShadow: `0px 0px 5px ${theme?.palette.otherFeatures.shadowColor}`,
    "& .tile-icon": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 50,
      height: 50,
      borderRadius: "50%",
      marginRight: 10,
    },
    "& .click-icon": {
      backgroundColor: theme?.palette.event.click.bgColor,
      svg: {
        color: theme?.palette.event.click.textColor,
      },
    },
    "& .installs-icon": {
      backgroundColor: theme?.palette.event.install.bgColor,
      svg: {
        color: theme?.palette.event.install.textColor,
      },
    },
    "& .cr-icon": {
      backgroundColor: theme?.palette.event.cr.bgColor,
      svg: {
        color: theme?.palette.event.cr.textColor,
      },
    },
    "& .sent-icon": {
      backgroundColor: theme?.palette.event.sent.bgColor,
      svg: {
        color: theme?.palette.event.sent.textColor,
      },
    },
    "& .tile-text": {
      "& h6": {
        fontWeight: 600,
        fontSize: 20,
        lineHeight: 1,
      },
      "& p": {
        lineHeight: 1,
        color: theme?.palette.text.gray,
        fontSize: 16,
      },
    },
  },
  ".event-box": {
    display: "flex",
    justifyContent: "right",
    gap: 10,
  },
  ".MuiFormControlLabel-root": {
    padding: theme?.spacing(0, 2),
  },
  ".check-box-checked": {
    width: "22px",
    height: "22px",
    border: `1px solid ${theme?.palette.otherFeatures.shadowColor}`,
  },
}));
